import { useEffect, type RefObject } from 'react';
import { useCameraStore } from '@/store/cameraStore';

/**
 * Keeps the camera store's viewport in sync with the element's CSS size and the
 * device pixel ratio. Size comes from ResizeObserver (no layout reads on our side);
 * DPR changes (browser zoom, moving between monitors) come from a resolution media query.
 */
export const useViewportObserver = (ref: RefObject<HTMLElement | null>): void => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let size = { width: 0, height: 0 };
    const publish = () => {
      useCameraStore.getState().setViewport({ size, devicePixelRatio: window.devicePixelRatio || 1 });
    };
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const box = entry.contentBoxSize[0];
      size = box
        ? { width: box.inlineSize, height: box.blockSize }
        : { width: entry.contentRect.width, height: entry.contentRect.height };
      publish();
    });
    observer.observe(el);

    // Initial measurement (ResizeObserver also fires once, but not synchronously).
    const rect = el.getBoundingClientRect();
    size = { width: rect.width, height: rect.height };
    publish();

    let dprQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    const onDpr = () => {
      dprQuery.removeEventListener('change', onDpr);
      dprQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      dprQuery.addEventListener('change', onDpr);
      publish();
    };
    dprQuery.addEventListener('change', onDpr);

    return () => {
      observer.disconnect();
      dprQuery.removeEventListener('change', onDpr);
    };
  }, [ref]);
};
